import { makeVar, useReactiveVar } from '@apollo/client';
import { useCallback, useMemo } from 'react';

export type LiveClassroomStudent = {
  studentId: number;
  isOnline: boolean;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  username?: string | null;
  emoji?: string | null;
  displayName: string;
};

// Reactive variable to store current live classroom students
export const liveClassroomStudents = makeVar<LiveClassroomStudent[]>([]);

export const useLiveClassroomStudents = () => {
  const students = useReactiveVar(liveClassroomStudents);

  const addOrUpdateStudent = useCallback((student: LiveClassroomStudent) => {
    const current = liveClassroomStudents();
    const existingIndex = current.findIndex(
      s => s.studentId === student.studentId
    );

    if (existingIndex >= 0) {
      // Update existing student
      const updated = [...current];
      updated[existingIndex] = student;
      liveClassroomStudents(updated);
    } else {
      // Add new student
      liveClassroomStudents([...current, student]);
    }
  }, []);

  const setStudentOnline = useCallback((studentId: number) => {
    const current = liveClassroomStudents();
    const updated = current.map(student =>
      student.studentId === studentId ? { ...student, isOnline: true } : student
    );
    liveClassroomStudents(updated);
  }, []);

  const setStudentOffline = useCallback((studentId: number) => {
    const current = liveClassroomStudents();
    const updated = current.map(student =>
      student.studentId === studentId
        ? { ...student, isOnline: false }
        : student
    );
    liveClassroomStudents(updated);
  }, []);

  const initialiseStudents = useCallback(
    (allStudents: LiveClassroomStudent[]) => {
      // Initialise all students as offline
      const initializedStudents: LiveClassroomStudent[] = allStudents.map(
        student => ({
          studentId: student.studentId,
          firstName: student.firstName,
          lastName: student.lastName,
          email: student.email,
          username: student.username,
          displayName: student.displayName,
          emoji: student.emoji,
          isOnline: false,
        })
      );
      liveClassroomStudents(initializedStudents);
    },
    []
  );

  const clearStudents = useCallback(() => {
    liveClassroomStudents([]);
  }, []);

  // Separate lists for online and offline students, sorted alphabetically
  const onlineStudents = useMemo(() => {
    return students
      .filter(s => s.isOnline)
      .sort((a, b) => {
        const nameA = a.displayName || '';
        const nameB = b.displayName || '';
        return nameA.localeCompare(nameB);
      });
  }, [students]);

  const offlineStudents = useMemo(() => {
    return students
      .filter(s => !s.isOnline)
      .sort((a, b) => {
        const nameA = a.displayName || '';
        const nameB = b.displayName || '';
        return nameA.localeCompare(nameB);
      });
  }, [students]);

  return {
    students,
    onlineStudents,
    offlineStudents,
    addOrUpdateStudent,
    setStudentOnline,
    setStudentOffline,
    initialiseStudents,
    clearStudents,
  };
};
